import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useApi } from '@/api/useApi'
import { useBaseStore } from '@haskoning/vue-basestore'
import API from '@/api/apiUrls'

const serviceURL = API.BUILDING_BLOCK.EQUIPMENT_FACTORS.INDEX

export interface EquipmentFactorDto {
  id: number
  category: string
  equipment_spec: string
  energy_carrier_type: string
  unit: string
  mki_phase_AD: string
  kg_co2eq_phase_AD: string
  transport_path: string | null
}

export type TransportLeaf = {
  energy_carrier_type: string
  unit: string
  mki_phase_AD: number | string
  kg_co2eq_phase_AD: number | string
  id?: number
}

export type TransportNode =
  | { category: string; values: TransportLeaf[] }
  | { category: string; values: Array<{ equipment_spec: string; values: TransportLeaf[] }> }

export const useEquipmentFactorStore = defineStore('equipmentFactor', () => {
  const { api } = useApi()
  const baseStore = useBaseStore<EquipmentFactorDto>({ service: serviceURL, api })

  const getCategories = computed(() => {
    const equipmentFactors = baseStore.items.value ?? []
    const categories = new Set<string>()

    for (const equipmentFactor of equipmentFactors) {
      categories.add(equipmentFactor.category)
    }

    return Array.from(categories)
  })

  const getEquipmentSpecs = (category: string) => {
    const equipmentFactors = baseStore.items.value ?? []
    const equipmentSpecs = new Set<string>()

    for (const equipmentFactor of equipmentFactors) {
      if (equipmentFactor.category === category) {
        equipmentSpecs.add(equipmentFactor.equipment_spec)
      }
    }

    return Array.from(equipmentSpecs)
  }

  const getEnergyCarrierTypes = (equipmentSpec: string, category: string) => {
    const equipmentFactors = baseStore.items.value ?? []
    const energyCarrierTypes = new Set<string>()

    for (const equipmentFactor of equipmentFactors) {
      if (
        equipmentFactor.equipment_spec === equipmentSpec &&
        equipmentFactor.category === category
      ) {
        energyCarrierTypes.add(equipmentFactor.energy_carrier_type)
      }
    }

    return Array.from(energyCarrierTypes)
  }

  const transportTree = computed<TransportNode[]>(() => {
    const byCategory: Record<string, EquipmentFactorDto[]> = {}
    for (const equipmentFactor of baseStore.items.value ?? []) {
      if ((equipmentFactor.category ?? '') === '') continue
      const categoryName = equipmentFactor.category
      if (!byCategory[categoryName]) byCategory[categoryName] = []
      byCategory[categoryName].push(equipmentFactor)
    }

    const result: TransportNode[] = []

    for (const [category, rows] of Object.entries(byCategory)) {
      // Determine if category has equipment_spec layer
      const equipmentSpecs = new Set<string>()

      for (const equipmentFactor of rows) {
        if (equipmentFactor.equipment_spec) {
          equipmentSpecs.add(equipmentFactor.equipment_spec)
        }
      }

      if (equipmentSpecs.size === 0) {
        // No spec layer, direct leaves under category
        const leaves: TransportLeaf[] = []

        for (const equipmentFactor of rows) {
          if ((equipmentFactor.energy_carrier_type ?? '') === '') continue
          leaves.push({
            energy_carrier_type: equipmentFactor.energy_carrier_type,
            unit: equipmentFactor.unit,
            mki_phase_AD: equipmentFactor.mki_phase_AD,
            kg_co2eq_phase_AD: equipmentFactor.kg_co2eq_phase_AD,
            id: equipmentFactor.id
          })
        }

        result.push({ category, values: leaves })
      } else {
        // Spec layer present
        const specNodes = []

        for (const equipmentSpec of equipmentSpecs) {
          const leaves: TransportLeaf[] = []

          for (const equipmentFactor of rows) {
            if (
              equipmentFactor.equipment_spec !== equipmentSpec ||
              (equipmentFactor.energy_carrier_type ?? '') === ''
            ) {
              continue
            }

            leaves.push({
              energy_carrier_type: equipmentFactor.energy_carrier_type,
              unit: equipmentFactor.unit,
              mki_phase_AD: equipmentFactor.mki_phase_AD,
              kg_co2eq_phase_AD: equipmentFactor.kg_co2eq_phase_AD,
              id: equipmentFactor.id
            })
          }

          specNodes.push({ equipment_spec: equipmentSpec, values: leaves })
        }

        result.push({ category, values: specNodes })
      }
    }
    return result
  })

  // Helper: find EquipmentFactor by path (category, optional spec, energy carrier)
  function findByPath(
    category: string,
    equipmentSpec: string | undefined,
    energyCarrierType: string
  ) {
    const equipmentFactors = baseStore.items.value ?? []
    return equipmentFactors.find(
      (equipmentFactor) =>
        equipmentFactor.category === category &&
        (equipmentSpec
          ? equipmentFactor.equipment_spec === equipmentSpec
          : equipmentFactor.equipment_spec === '' ||
            equipmentFactor.equipment_spec === undefined) &&
        equipmentFactor.energy_carrier_type === energyCarrierType
    )
  }

  return {
    ...baseStore,
    getCategories,
    getEquipmentSpecs,
    getEnergyCarrierTypes,
    transportTree,
    findByPath
  }
})
